//#region INTERFACE IRemovalRow

import {Timestamp} from '@angular/fire/firestore';
import {ColDef, GridOptions} from 'ag-grid-community';

/**
 * ### Interface IRemovalRow
 * A single line-item that appears in the *Bulk Removal* confirmation dialog or
 * similar UI.
 *
 * @interface IRemovalRow
 * @extends Object
 * @description Describes the supplier scheduled for removal on a particular day,
 *              capturing its display name, unique code, and an optional reason
 *              explaining the removal.
 *
 * @example
 * const removalRow: IRemovalRow = {
 *   day: 'Wednesday',
 *   name: 'Acme Supplies',
 *   supplier: 'ACM-001',
 *   reason: 'No Stock Items'
 * };
 */
export interface IRemovalRow extends Object {
  /**
   * The day of the week on which the supplier removal is to take place.
   *
   * @type {string}
   * @example 'Monday'
   */
  day: string;

  facetCounts: number;

  /**
   * Human-readable supplier name shown in the UI.
   *
   * @type {string}
   * @example 'Fresh Farms'
   */
  name: string;

  /**
   * Unique supplier code or identifier used internally.
   *
   * @type {string}
   * @example 'FRF-123'
   */
  supplier: string;

  /**
   * Optional explanation for why the supplier is being removed.
   *
   * @type {string | undefined}
   * @default undefined
   * @example 'On Hold'
   */
  reason?: string;
}

//#endregion

//#region Scheduled Order Grid

// todo look at implements order account settings
export interface IScheduledSupplierGrid {
  day: string;
  dayKey: string;
  growth: number;
  isSelected: boolean;
  items: number;
  monthsHistory: number;
  period: number;
  periodName: string;
  repeat: string;
  stockDays: number;
  supplier: string;
  supplierId: string;
  supplierName: string;
  modifiedDate?: Timestamp;
  nextOrder?: string;
  startDate?: Date;
}

export type IFilterModel = Record<string, {
  filterType: string;
  type: string;
  filter: string | number;
}>;

/**
 * Base column definitions for the supplier grid in old-models schedule.
 * Note: Component-specific cellRenderer functions should be added in the component.
 */
export const BASE_SUPPLIER_GRID_COL_DEFS: ColDef[] = [
  {
    field: 'icons',
    headerName: '',
    pinned: 'left',
    resizable: false,
    lockPinned: true,
    lockPosition: true,
    lockVisible: true,
    hide: false,
    editable: false,
    maxWidth: 60,
    cellEditor: false,
    suppressHeaderMenuButton: true,
  },
  {
    field: 'supplierId',
    headerName: 'Supplier Code',
    pinned: 'left',
    resizable: true,
    lockPinned: true,
    lockPosition: true,
    lockVisible: true,
    editable: false,
    maxWidth: 150,
    cellEditor: false,
    suppressHeaderMenuButton: true,
  },
  {
    field: 'account',
    headerName: 'Account',
    resizable: true,
    editable: false,
    cellEditor: false,
    suppressHeaderMenuButton: true,
    hide: true,
  },
  {
    field: 'name',
    headerName: 'Supplier Name',
    lockPinned: true,
    resizable: true,
    lockVisible: true,
    editable: false,
    cellEditor: false,
  },
  {
    field: 'contact',
    headerName: 'Contact',
    resizable: true,
    editable: false,
    cellEditor: false,
    suppressHeaderMenuButton: true,
    hide: true,
  },
  {
    field: 'tel1',
    headerName: 'Phone 1',
    resizable: true,
    editable: false,
    cellEditor: false,
    suppressHeaderMenuButton: true,
    hide: true,
  },
  {
    field: 'cell',
    headerName: 'Cell',
    resizable: true,
    editable: false,
    cellEditor: false,
    suppressHeaderMenuButton: true,
    hide: true,
  },
  {
    field: 'email',
    headerName: 'Email',
    resizable: true,
    editable: false,
    cellEditor: false,
    suppressHeaderMenuButton: true,
    hide: true,
  },
  {
    field: 'taxNo',
    headerName: 'Tax Number',
    resizable: true,
    editable: false,
    cellEditor: false,
    suppressHeaderMenuButton: true,
    hide: true,
  },
  {
    field: 'linkAcc',
    headerName: 'Linked Account',
    resizable: true,
    editable: false,
    cellEditor: false,
    suppressHeaderMenuButton: true,
    hide: true,
  },
  {
    field: 'terms',
    headerName: 'Terms',
    type: ['numericColumn', 'alignRight'],
    resizable: true,
    editable: false,
    cellEditor: false,
    suppressHeaderMenuButton: true,
    hide: true,
  },
  {
    field: 'age',
    headerName: 'Age',
    type: ['numericColumn', 'alignRight'],
    resizable: true,
    editable: false,
    cellEditor: false,
    suppressHeaderMenuButton: true,
    hide: true,
  },
  {
    field: 'crLimit',
    headerName: 'Credit Limit',
    type: ['numericColumn', 'alignRight'],
    resizable: true,
    editable: false,
    cellEditor: false,
    suppressHeaderMenuButton: true,
    hide: true,
  },
  {
    field: 'total',
    headerName: 'Total Owed',
    type: ['numericColumn', 'alignRight'],
    resizable: true,
    editable: false,
    cellEditor: false,
    suppressHeaderMenuButton: true,
    hide: true,
  },
  {
    field: 'onHoldCode',
    headerName: 'On Hold Code',
    type: ['numericColumn', 'alignRight'],
    resizable: true,
    editable: false,
    cellEditor: false,
    suppressHeaderMenuButton: true,
    hide: false,
  },
  {
    field: 'minOrder',
    headerName: 'Min Order',
    type: ['numericColumn', 'alignRight'],
    resizable: true,
    editable: false,
    cellEditor: false,
    suppressHeaderMenuButton: true,
    hide: false,
  },
  {
    field: 'itemCount',
    headerName: 'Stock Item Count',
    lockPinned: true,
    resizable: true,
    sortable: false,
    type: ['alignRight'],
    editable: false,
    cellEditor: false,
    suppressHeaderMenuButton: true,
  },
];

/**
 * Base grid options for the supplier grid in old-models schedule.
 * Note: Component-specific functions like getRowStyle should be added in the component.
 */
export const BASE_SUPPLIER_GRID_OPTIONS: GridOptions = {
  colResizeDefault: 'shift',
  debug: false,
  editType: null,
  enterNavigatesVertically: true,
  enterNavigatesVerticallyAfterEdit: true,
  suppressAnimationFrame: true,
  enableCellTextSelection: false,
  multiSortKey: 'ctrl',
  rowBuffer: 100,
  pagination: false, // Disabled pagination to avoid conflict with rowDragManaged
  rowHeight: 45,
  suppressScrollOnNewData: true,
  rowDragManaged: true,
  rowDragEntireRow: true,
  rowDragMultiRow: true,
  tooltipShowDelay: 500,
  tooltipInteraction: true,
  rowSelection: {
    mode: 'multiRow',
    checkboxes: true,
    headerCheckbox: true,
    selectAll: 'filtered',
    enableClickSelection: true,
    enableSelectionWithoutKeys: true,
    isRowSelectable: (node) => !!node.data,
  },
  selectionColumnDef: {
    headerName: '',
    pinned: 'right',
    lockPosition: 'right',
    lockPinned: true,
    suppressMovable: true,
    sortable: false,
    cellStyle: {
      display: 'flex',
      margin: '0 0 -5px 0',
    },
    resizable: false,
    minWidth: 45,
    maxWidth: 45,
    width: 45,
  },
  singleClickEdit: true,
  stopEditingWhenCellsLoseFocus: true,
  undoRedoCellEditing: false,
  undoRedoCellEditingLimit: 25,
  suppressContextMenu: true,
  defaultColDef: {
    filter: true,
    suppressHeaderMenuButton: true,
    floatingFilter: false,
    suppressHeaderFilterButton: true,
  },
};

const WEEK_ORDER: Record<string, number> = {
  monday: 0,
  tuesday: 1,
  wednesday: 2,
  thursday: 3,
  friday: 4,
  saturday: 5,
  sunday: 6,
};

const getDayIndex = (val: unknown): number => {
  if (val == null) return Number.POSITIVE_INFINITY;
  const key = String(val).trim().toLowerCase();
  return WEEK_ORDER[key] ?? Number.POSITIVE_INFINITY;
};

const dayComparator = (a?: string, b?: string): number =>
  getDayIndex(a) - getDayIndex(b);

export const BASE_ASSIGNMENT_COL_DEFS: ColDef<IScheduledSupplierGrid>[] = [
  {
    field: 'day',
    headerName: 'Day',
    pinned: 'left',
    sortable: true,
    filter: true,
    suppressHeaderFilterButton: true,
    suppressHeaderMenuButton: true,
    comparator: dayComparator,
    resizable: true,
    minWidth: 100,
    flex: 1,
  },
  {
    field: 'supplier',
    headerName: 'Supplier',
    sortable: true,
    filter: true,
    suppressHeaderMenuButton: true,
    suppressFloatingFilterButton: true,
    suppressHeaderFilterButton: true,
    resizable: true,
    minWidth: 100,
    flex: 1,
  },
  {
    field: 'repeat',
    headerName: 'Repeat',
    sortable: true,
    filter: true,
    resizable: true,
    suppressHeaderMenuButton: true,
    suppressFloatingFilterButton: true,
    suppressHeaderFilterButton: true,
    minWidth: 100,
    flex: 1,
  },
  {
    field: 'stockDays',
    headerName: 'Stock Days',
    sortable: true,
    resizable: true,
    filter: true,
    suppressHeaderMenuButton: true,
    suppressFloatingFilterButton: true,
    suppressHeaderFilterButton: true,
    flex: 1,
    minWidth: 100,
    cellStyle: {textAlign: 'right'},
  },
  {
    field: 'growth',
    headerName: 'Growth',
    sortable: true,
    resizable: true,
    filter: true,
    flex: 1,
    suppressHeaderMenuButton: true,
    suppressHeaderFilterButton: true,
    cellStyle: {textAlign: 'right'},
    minWidth: 100,
  },
  {
    field: 'periodName',
    headerName: 'Period',
    sortable: true,
    resizable: true,
    filter: true,
    suppressHeaderMenuButton: true,
    suppressFloatingFilterButton: true,
    suppressHeaderFilterButton: true,
    flex: 1,
    minWidth: 100,
  }, {
    field: 'monthsHistory',
    headerName: 'Months History',
    sortable: true,
    resizable: true,
    filter: true,
    suppressHeaderMenuButton: true,
    suppressFloatingFilterButton: true,
    suppressHeaderFilterButton: true,
    flex: 1,
    minWidth: 100,
    cellStyle: {textAlign: 'right'},
  }, {
    field: 'startDate',
    headerName: 'First Order Date',
    sortable: true,
    resizable: true,
    filter: true,
    suppressHeaderFilterButton: true,
    suppressHeaderMenuButton: true,
    flex: 1,
    minWidth: 120,
  }, {
    field: 'nextOrder',
    headerName: 'Next Order',
    sortable: false,
    resizable: true,
    filter: false,
    suppressHeaderFilterButton: true,
    suppressHeaderMenuButton: true,
    flex: 1,
    minWidth: 120,
  }, {
    field: 'modifiedDate',
    headerName: 'Last Modified',
    sortable: true,
    resizable: true,
    filter: true,
    suppressHeaderFilterButton: true,
    suppressHeaderMenuButton: true,
    flex: 1,
    minWidth: 100,
  },
  {
    field: 'items',
    headerName: 'Items',
    sortable: true,
    resizable: true,
    filter: true,
    suppressHeaderFilterButton: true,
    suppressHeaderMenuButton: true,
    flex: 1,
    minWidth: 80,
    cellStyle: {textAlign: 'right'},
  },
];


//#endregion
